/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useRef } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useAuth } from '@/providers/auth';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';
import { pushCreatedLead } from '@/lib/gtm/events/page';
import { emitWertAccountCreation, emitWertCreatedLead } from '@/lib/adapters/engagement/wert';
import { createLogger } from '@/lib/logger';

/**
 * Fires created_lead GTM event when URL contains created_lead=1 (signup or checkout guest registration).
 * Runs once per page load when GTM is enabled and consent given.
 */
export function CreatedLeadTracker() {
    const config = useConfig<AppConfig>();
    const { trackingConsent } = useTrackingConsent();
    const auth = useAuth();
    const firedRef = useRef(false);

    const gtmEnabled = config.engagement?.gtm?.enabled ?? false;
    const customerId = auth?.customerId;

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (firedRef.current) return;
        if (!gtmEnabled || trackingConsent !== TrackingConsent.Accepted) return;

        const params = new URLSearchParams(window.location.search);
        const createdLead = params.get('created_lead') === '1';
        const accountCreated = params.get('accountCreated') === 'true';
        // INT-21e Phase 6 — server action mints a `lead_token` UUID and
        // threads it via the redirect URL. Reading it here lets the browser
        // path fire the SAME `wert_lead_${token}` event_id as the server
        // enqueue — Gather API dedupes across both transports.
        const leadSubmissionToken = params.get('lead_token') ?? undefined;

        if (createdLead || accountCreated) {
            firedRef.current = true;
            const widget = accountCreated ? 'checkout' : 'create account';
            pushCreatedLead({ widget });

            // The runtime `AnalyticsEvent` union does not include `created_lead`,
            // so the mediator never fans this event to the WERT adapter —
            // direct-fire alongside GA. The WERT adapter has its own consent
            // guard inside; the outer guard above is belt-and-braces.
            try {
                emitWertCreatedLead({ widget, leadSubmissionToken });
            } catch (error) {
                // Analytics fail-open: WERT never crashes the confirmation page.
                createLogger({ scope: 'created-lead-tracker' }).warn('WERT created_lead emit failed', { error });
            }

            // Phase 4 (INT-21c) — `account_creation` is a distinct WERT event
            // from `created_lead` (see plan §10 P4.1 and ticket §280-290;
            // dashboards subscribe to both). Fire alongside `created_lead` on
            // the two paths that actually create an account:
            //   - standalone signup: URL carries `created_lead=1` (no
            //     `accountCreated` flag) — the returning shopper is now
            //     registered per the auth provider.
            //   - checkout guest registration: URL carries `accountCreated=true`.
            // The direct-fire helper's `wert_account_created_${customerId}`
            // sessionStorage guard catches a page refresh so we don't re-emit.
            //
            // TODO(INT-21c Phase 4.5): resolve `emailHash` / `phoneHash` from
            // the server. Options: (a) surface hashes in the signup action
            // response and thread them via cookie / URL fragment to this
            // tracker; (b) add hashes to `PublicSessionData` on register /
            // login so `useAuth()` exposes them; (c) call a resolve-identity
            // endpoint on mount. Ticket §280-320 landed the server-side
            // hashing helper for `created_lead`; extending it to
            // `account_creation` is a small follow-up.
            const isRegistered = auth?.userType === 'registered';
            const shouldEmitAccountCreation = isRegistered && Boolean(customerId) && (accountCreated || createdLead);
            if (shouldEmitAccountCreation) {
                try {
                    emitWertAccountCreation({
                        customerId,
                        signupSource: accountCreated ? 'checkout_guest' : 'unknown',
                        // Registration flow does not (yet) capture the marketing-
                        // consent checkbox state in a form field readable here;
                        // default `false`. TODO(INT-21c Phase 4.5): thread the
                        // consent flag via URL param or cookie so we can emit
                        // an accurate value.
                        marketingConsent: false,
                    });
                } catch (error) {
                    // Analytics fail-open.
                    createLogger({ scope: 'created-lead-tracker' }).warn('WERT account_creation emit failed', {
                        error,
                    });
                }
            }
        }
    }, [gtmEnabled, trackingConsent, auth?.userType, customerId]);

    return null;
}
